import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SITE, NAV_LINKS } from "@/config/site";

const Footer = () => (
    <footer data-testid="site-footer" className="border-t border-border bg-espresso text-cream/85">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:grid-cols-4 md:px-8">
            <div className="md:col-span-2">
                <p className="font-serif text-2xl font-medium text-cream">Equilibrio del Sé - {SITE.brand}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-cream/50">{SITE.brandSuffix}</p>
                <p className="mt-5 max-w-sm text-sm leading-relaxed text-cream/70">
                    Uno studio olistico intimo nel cuore di Bologna, dove il tempo rallenta e il corpo
                    ritrova spazio per respirare. Ogni trattamento nasce dall’ascolto.
                </p>
                <div className="mt-6 flex gap-3">
                    <a
                        href={SITE.instagramUrl}
                        data-testid="footer-social-instagram"
                        aria-label="Instagram"
                        className="rounded-full border border-cream/25 p-2.5 transition-colors duration-300 hover:border-cream hover:text-cream"
                    >
                        <Instagram className="h-4 w-4" strokeWidth={1.5} />
                    </a>
                    <a
                        href={SITE.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-social-maps"
                        aria-label="Google Maps"
                        className="rounded-full border border-cream/25 p-2.5 transition-colors duration-300 hover:border-cream hover:text-cream"
                    >
                        <MapPin className="h-4 w-4" strokeWidth={1.5} />
                    </a>
                </div>
            </div>

            <div>
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-cream/50">Contatti</p>
                <ul className="mt-5 space-y-3 text-sm">
                    <li className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-beige" strokeWidth={1.5} />
                        <span data-testid="footer-address">{SITE.address}</span>
                    </li>
                    <li className="flex items-center gap-3">
                        <Phone className="h-4 w-4 shrink-0 text-beige" strokeWidth={1.5} />
                        <a href={SITE.phoneHref} data-testid="footer-phone" className="transition-colors hover:text-cream">
                            {SITE.phoneDisplay}
                        </a>
                    </li>
                    <li className="flex items-center gap-3">
                        <Mail className="h-4 w-4 shrink-0 text-beige" strokeWidth={1.5} />
                        <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="transition-colors hover:text-cream">
                            {SITE.email}
                        </a>
                    </li>
                </ul>
            </div>

            <div>
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-cream/50">Orari</p>
                <ul className="mt-5 space-y-3 text-sm" data-testid="footer-hours">
                    {SITE.hours.map((h) => (
                        <li key={h.days} className="flex items-start gap-3">
                            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-beige" strokeWidth={1.5} />
                            <span>
                                {h.days}
                                <span className="block text-cream/60">{h.time}</span>
                            </span>
                        </li>
                    ))}
                </ul>
                <Link
                    to="/prenota"
                    data-testid="footer-cta-prenota"
                    className="mt-6 inline-block rounded-full bg-cream px-6 py-2.5 text-sm font-medium text-espresso transition-colors duration-300 hover:bg-beige"
                >
                    Prenota
                </Link>
            </div>
        </div>

        <div className="border-t border-cream/10">
            <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-6 text-xs text-cream/50 md:flex-row md:px-8">
                <p>© {new Date().getFullYear()} Equilibrio del Sé - {SITE.brand} · {SITE.brandSuffix}. Tutti i diritti riservati.</p>
                <div className="flex items-center gap-5">
                    {NAV_LINKS.slice(1, 3).map((l) => (
                        <Link key={l.path} to={l.path} className="transition-colors hover:text-cream">
                            {l.label}
                        </Link>
                    ))}
                    <Dialog>
                        <DialogTrigger asChild>
                            <button data-testid="footer-privacy-button" className="transition-colors hover:text-cream">
                                Privacy Policy
                            </button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[80vh] overflow-y-auto bg-cream" data-testid="footer-privacy-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl text-ink">Privacy Policy</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 text-sm leading-relaxed text-clay">
                                <p>
                                    <strong className="text-ink">Testo in aggiornamento.</strong> Questa informativa è
                                    un segnaposto e verrà completata con l’informativa privacy definitiva dello studio.
                                </p>
                                <p>
                                    I dati inseriti nel modulo di prenotazione (nome, cognome, email, telefono) sono
                                    utilizzati esclusivamente per gestire l’appuntamento richiesto e per inviare le
                                    relative comunicazioni. Non vengono ceduti a terzi.
                                </p>
                                <p>
                                    Per qualsiasi richiesta relativa ai tuoi dati puoi scrivere a{" "}
                                    <a href={`mailto:${SITE.email}`} className="text-sage underline">
                                        {SITE.email}
                                    </a>
                                    .
                                </p>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </div>
    </footer>
);

export default Footer;
